import {DateRange} from "@mui/x-date-pickers-pro";
import {DateTime} from "luxon";
import {
    DateOperator,
    parseDateOperatorValues,
    removeDateOperatorValue,
    upsertDateOperatorValue,
} from "../../../utils/filterDateOperatorUtils";
import {parseNumberRangeValue} from "../../../utils/filterNumberRangeUtils";

export interface FilterOption {
    item: string;
    checked: boolean;
    categoryId: string;
    label?: string;
    subItems?: FilterOption[];
    cascadeOnly?: boolean;
    dateRange?: DateRange<DateTime>;
    dateValue?: DateTime | null;
    dateOperator?: DateOperator;
    numberRange?: [number, number];
    rangeMin?: number;
    rangeMax?: number;
    rangeStep?: number;
}

export interface IncludeExcludeFilterCategory {
    id: string;
    label: string;
    type?: "checkbox" | "date-range" | "date-operator" | "number-range";
    options: FilterOption[];
    serverSideSearch?: boolean; // NEW: flag for server-side search support
    preserveOptionOrder?: boolean; // keep options in the supplied order instead of sorting alphabetically
    includeOnly?: boolean; // When true, hide exclude tab for this category
    valueFormat?: "currency" | "number"; // How number-range values are rendered in chips
    partialOptions?: boolean; // options list only some values, so selecting every option doesn't mean "Any"
}


export type FilterAction =
    | {
    type: "INIT";
    payload: {
        categories: IncludeExcludeFilterCategory[];
        existingFilters?: {
            include: Record<string, string[] | DateRange<DateTime>>;
            exclude: Record<string, string[] | DateRange<DateTime>>;
        };
    };
}
    | {
    type: "UPDATE_OPTIONS";
    payload: {
        categories: IncludeExcludeFilterCategory[];
    };
}
    | {
    type: "TOGGLE_OPTION";
    payload: {
        filterType: "include" | "exclude";
        topLevelCategoryId: string;
        categoryId: string;
        optionId: string;
    };
}
    | {
    type: "SET_DATE_RANGE";
    payload: {
        filterType: "include" | "exclude";
        parentCategoryId: string;
        categoryId: string;
        dateRange: DateRange<DateTime> | null;
    };
}
    | {
    type: "CLEAR_DATE_RANGE";
    payload: {
        filterType: "include" | "exclude";
        parentCategoryId: string;
        categoryId: string;
    };
}
    | {
    type: "SET_DATE_OPERATOR";
    payload: {
        filterType: "include" | "exclude";
        categoryId: string;
        operator: DateOperator;
        dateValue: DateTime | null;
    };
}
    | {
    type: "REMOVE_FILTER";
    payload: {
        filterType: "include" | "exclude";
        categoryId: string;
        optionId: string;
    };
}
    | {
    type: "SET_NUMBER_RANGE";
    payload: {
        filterType: "include" | "exclude";
        parentCategoryId: string;
        categoryId: string;
        numberRange: [number, number];
    };
}
    | {
    type: "CLEAR_NUMBER_RANGE";
    payload: {
        filterType: "include" | "exclude";
        parentCategoryId: string;
        categoryId: string;
    };
}
    | {
    type: "CLEAR_FILTER_TYPE";
    payload: { filterType: "include" | "exclude"; categoryId: string };
}
    | { type: "CLEAR_ALL" }
    | {
    type: "SELECT_ALL";
    payload: { filterType: "include" | "exclude"; categoryId: string };
}
    | {
    type: "SET_SERVER_OPTIONS";
    payload: {
        filterType: "include" | "exclude";
        categoryId: string;
        options: FilterOption[];
    };
}
    | {
    type: "CLEAR_SERVER_OPTIONS";
    payload: {
        filterType: "include" | "exclude";
        categoryId: string;
    };
};

export interface FilterState {
    options: {
        include: IncludeExcludeFilterCategory[];
        exclude: IncludeExcludeFilterCategory[];
    };
    filters: {
        include: Record<string, string[] | DateRange<DateTime>>;
        exclude: Record<string, string[] | DateRange<DateTime>>;
    };
    isInitializing: boolean;
    /**
     * Chip consolidation mode (MER-311). Presentation-only: it controls how
     * chips render (`"consolidated"` collapses to one chip per attribute, with
     * a combined `+N / -M` chip when an attribute has both include and exclude
     * values) and does not alter the include/exclude data. Both maps coexist in
     * either mode; INIT preserves whatever a saved view carries on both sides.
     * Defaults to `"legacy"`.
     */
    chipDisplayMode?: "legacy" | "consolidated";
}

function addCheckedProperty(
    options: FilterOption[],
    existingFilters?: Record<string, string[]>
): FilterOption[] {
    if (!existingFilters || Object.keys(existingFilters).length === 0) {
        return options
    }

    return options.map((opt) => ({
        ...opt,
        checked: existingFilters?.[opt.categoryId]?.includes(opt.item) ?? false,
        subItems: opt.subItems
            ? addCheckedProperty(opt.subItems, existingFilters)
            : undefined,
    }));
}

function addDateMetadata(
    category: IncludeExcludeFilterCategory,
    existingFilters?: Record<string, string[] | DateRange<DateTime>>,
): FilterOption[] {
    if (category.type === "date-range") {
        const existingDateRange = existingFilters?.[category.id];
        const hydratedDateRange = Array.isArray(existingDateRange) &&
            existingDateRange.length === 2 &&
            typeof existingDateRange[0] === "string" &&
            typeof existingDateRange[1] === "string"
            ? [
                DateTime.fromISO(existingDateRange[0], {zone: "utc"}),
                DateTime.fromISO(existingDateRange[1], {zone: "utc"}),
            ] as DateRange<DateTime>
            : undefined;

        return category.options.map((option) => ({
            ...option,
            dateRange: hydratedDateRange,
        }));
    }

    if (category.type === "date-operator") {
        const existingDateOperatorValues = existingFilters?.[category.id];
        const dateOperatorValues = Array.isArray(existingDateOperatorValues) &&
            existingDateOperatorValues.every((value) => typeof value === "string")
            ? existingDateOperatorValues
            : undefined;
        const parsedValues = parseDateOperatorValues(dateOperatorValues);

        return category.options.map((option) => {
            const operator = option.dateOperator ?? (option.item.toLowerCase() as DateOperator);
            const isoDate = parsedValues[operator];

            return {
                ...option,
                dateOperator: operator,
                dateValue: isoDate ? DateTime.fromISO(isoDate, {zone: "utc"}) : null,
            };
        });
    }

    if (category.type === "number-range") {
        const existingRange = existingFilters?.[category.id];
        const hydratedRange = Array.isArray(existingRange) &&
            existingRange.length === 2 &&
            typeof existingRange[0] === "string" &&
            typeof existingRange[1] === "string"
            ? parseNumberRangeValue(existingRange) ?? undefined
            : undefined;

        return category.options.map((option) => ({
            ...option,
            numberRange: hydratedRange,
        }));
    }

    return addCheckedProperty(category.options, existingFilters as Record<string, string[]>);
}

function hydrateCategory(
    category: IncludeExcludeFilterCategory,
    existingFilters?: Record<string, string[] | DateRange<DateTime>>,
): IncludeExcludeFilterCategory {
    return {
        ...category,
        options: addDateMetadata(category, existingFilters),
    };
}

const isYearValid = (dt: DateTime) =>
    dt.isValid &&
    dt.year >= DateTime.now().year - 50 &&
    dt.year <= DateTime.now().year + 25;

// Recursively toggle checked state for a FilterOption and cascade to its subItems
function setOrToggleCheckedRecursive(options: FilterOption[], optionId: string, checkedValue?: boolean): FilterOption[] {
    return options.map((opt) => {
        if (opt.item === optionId) {
            const newChecked = checkedValue ?? !(opt.checked ?? false);
            return {
                ...opt,
                checked: newChecked,
                // Cascade: set all children to the same checked state
                subItems: opt.subItems ? setCheckedRecursive(opt.subItems, newChecked) : undefined,
            };
        }
        if (opt.subItems && opt.subItems.length > 0) {
            return {...opt, subItems: setOrToggleCheckedRecursive(opt.subItems, optionId, checkedValue)};
        }
        return opt;
    });
}

function setCheckedRecursive(options: FilterOption[], checked: boolean): FilterOption[] {
    return options.map((opt) => ({
        ...opt,
        checked,
        subItems: opt.subItems ? setCheckedRecursive(opt.subItems, checked) : undefined,
    }));
}

/** Collect all descendant item IDs grouped by categoryId. */
function collectDescendantIdsByCategory(subItems: FilterOption[]): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    for (const opt of subItems) {
        if (!opt.cascadeOnly) {
            if (!result[opt.categoryId]) {
                result[opt.categoryId] = [];
            }
            result[opt.categoryId].push(opt.item);
        }
        if (opt.subItems) {
            const childIds = collectDescendantIdsByCategory(opt.subItems);
            for (const [catId, ids] of Object.entries(childIds)) {
                if (!result[catId]) {
                    result[catId] = [];
                }
                result[catId].push(...ids);
            }
        }
    }
    return result;
}

/** Find a FilterOption by item ID anywhere in the tree. */
function findOptionRecursive(options: FilterOption[], optionId: string): FilterOption | undefined {
    for (const opt of options) {
        if (opt.item === optionId) return opt;
        if (opt.subItems) {
            const found = findOptionRecursive(opt.subItems, optionId);
            if (found) return found;
        }
    }
    return undefined;
}

// Handles matching the category and delegating to the recursive toggle
const updatedCheckboxOptionsToggle = (
    options: IncludeExcludeFilterCategory[],
    categoryId: string,
    optionId: string
): IncludeExcludeFilterCategory[] => {

    return options.map((cat) => {
        if (cat.id !== categoryId) return cat;
        return {
            ...cat,
            options: setOrToggleCheckedRecursive(cat.options, optionId),
        };
    });
};

const updatedCheckboxOptionsSetChecked = (
    options: IncludeExcludeFilterCategory[],
    categoryId: string | undefined,
    optionId: string,
    checked: boolean
): IncludeExcludeFilterCategory[] => {
    return options.map((cat) => {
        if (cat.id !== categoryId) return cat;
        return {
            ...cat,
            options: setOrToggleCheckedRecursive(cat.options, optionId, checked),
        };
    });
};

const updatedDateOptions = (
    options: IncludeExcludeFilterCategory[],
    parentCategoryId: string,
    categoryId: string,
    dateRange?: DateRange<DateTime>
): IncludeExcludeFilterCategory[] => {
    return options.map((cat) => {
        if (cat.id !== parentCategoryId) return cat;
        return {
            ...cat,
            options: cat.options.map((opt) =>
                opt.categoryId === categoryId
                    ? {...opt, dateRange: dateRange}
                    : opt
            ),
        };
    });
}

const updatedNumberRangeOptions = (
    options: IncludeExcludeFilterCategory[],
    parentCategoryId: string,
    categoryId: string,
    numberRange?: [number, number]
): IncludeExcludeFilterCategory[] => {
    return options.map((cat) => {
        if (cat.id !== parentCategoryId) return cat;
        return {
            ...cat,
            options: cat.options.map((opt) =>
                opt.categoryId === categoryId
                    ? {...opt, numberRange: numberRange}
                    : opt
            ),
        };
    });
}

const updatedDateOperatorOptions = (
    options: IncludeExcludeFilterCategory[],
    categoryId: string,
    operator: DateOperator,
    dateValue: DateTime | null,
): IncludeExcludeFilterCategory[] => {
    return options.map((category) => {
        if (category.id !== categoryId) {
            return category;
        }

        return {
            ...category,
            options: category.options.map((option) => {
                const optionOperator = option.dateOperator ?? (option.item.toLowerCase() as DateOperator);

                return optionOperator === operator
                    ? {...option, dateOperator: optionOperator, dateValue}
                    : option;
            }),
        };
    });
};

function resetCategoryOptions(category: IncludeExcludeFilterCategory): IncludeExcludeFilterCategory {
    if (category.type === "date-range") {
        return {
            ...category,
            options: category.options.map((option) => ({
                ...option,
                dateRange: undefined,
            })),
        };
    }

    if (category.type === "date-operator") {
        return {
            ...category,
            options: category.options.map((option) => ({
                ...option,
                dateValue: null,
            })),
        };
    }

    if (category.type === "number-range") {
        return {
            ...category,
            options: category.options.map((option) => ({
                ...option,
                numberRange: undefined,
            })),
        };
    }

    return {
        ...category,
        options: setCheckedRecursive(category.options, false),
    };
}

// --- Case Handlers ---
function handleInit(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "INIT") return state;
    const {categories, existingFilters} = action.payload;

    const seedFilters = {
        include: existingFilters?.include || {},
        exclude: existingFilters?.exclude || {},
    };

    const buildOptions = (type: "include" | "exclude") =>
                categories.map((category) => hydrateCategory(category, seedFilters[type]));
    return {
        options: {
            include: buildOptions("include"),
            exclude: buildOptions("exclude"),
        },
        filters: {
            include: seedFilters.include,
            exclude: seedFilters.exclude,
        },
        isInitializing: false,
        chipDisplayMode: state.chipDisplayMode,
    };
}

function handleUpdateOptions(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "UPDATE_OPTIONS") return state;
    const {categories} = action.payload;
    const buildOptions = (type: "include" | "exclude") =>
        categories.map((category) => hydrateCategory(category, state.filters[type]));
    return {
        ...state,
        options: {
            include: buildOptions("include"),
            exclude: buildOptions("exclude"),
        },
    };
}

function handleSetDateOperator(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "SET_DATE_OPERATOR") return state;
    const {filterType, categoryId, operator, dateValue} = action.payload;
    const currentValues = (state.filters[filterType][categoryId] as string[]) || [];

    const nextValues = dateValue
        ? upsertDateOperatorValue(currentValues, operator, dateValue.toISODate()!)
        : removeDateOperatorValue(currentValues, operator);

    if (dateValue && !isYearValid(dateValue)) {
        return state;
    }

    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: updatedDateOperatorOptions(
                state.options[filterType],
                categoryId,
                operator,
                dateValue,
            ),
        },
        filters: {
            ...state.filters,
            [filterType]: {
                ...state.filters[filterType],
                [categoryId]: nextValues,
            },
        },
    };
}

function handleToggleOption(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "TOGGLE_OPTION") return state;
    const {filterType, topLevelCategoryId, categoryId, optionId} = action.payload;
    const oppositeType = filterType === "include" ? "exclude" : "include";

    // Find the option BEFORE toggling to determine the new checked state
    const category = state.options[filterType].find((cat) => cat.id === topLevelCategoryId);
    const clickedOption = category ? findOptionRecursive(category.options, optionId) : undefined;
    const clickedIsCascadeOnly = !!clickedOption?.cascadeOnly;

    // A cascade-only node has no own value, so its displayed tri-state is driven
    // entirely by descendant selection (computeNodeCheckState). Its own `checked`
    // flag is never set when leaves are checked individually, so deriving the
    // toggle direction from that flag would re-apply checks on a node that shows
    // as checked. Mirror the popover's cascadeToggleDraft: derive direction from
    // whether every descendant leaf is already selected.
    let willBeChecked: boolean;
    if (clickedIsCascadeOnly && clickedOption?.subItems?.length) {
        const descendantsByCategory = collectDescendantIdsByCategory(clickedOption.subItems);
        const descendants = Object.entries(descendantsByCategory).flatMap(([descCategoryId, ids]) =>
            ids.map((id) => ({categoryId: descCategoryId, id})),
        );
        const allSelected =
            descendants.length > 0 &&
            descendants.every(({categoryId: descCategoryId, id}) =>
                ((state.filters[filterType][descCategoryId] as string[]) ?? []).includes(id),
            );
        willBeChecked = descendants.length > 0 && !allSelected;
    } else {
        willBeChecked = !(clickedOption?.checked ?? false);
    }

    const updatedOptions = updatedCheckboxOptionsToggle(
        state.options[filterType],
        topLevelCategoryId,
        optionId
    );
    const oppositeUpdated = updatedCheckboxOptionsSetChecked(
        state.options[oppositeType],
        topLevelCategoryId,
        optionId,
        false
    );

    // Update filters for the clicked item itself (skip if cascadeOnly)
    const isCascadeOnly = clickedIsCascadeOnly;
    const current = (state.filters[filterType][categoryId] as string[]) || [];
    const oppositeCurrent =
        (state.filters[oppositeType][categoryId] as string[]) || [];

    let newFilter: string[];
    let newOpposite: string[];
    if (isCascadeOnly) {
        newFilter = current;
        newOpposite = oppositeCurrent;
    } else {
        const isChecked = current.includes(optionId);
        newFilter = isChecked
            ? current.filter((id) => id !== optionId)
            : [...current, optionId];
        newOpposite = oppositeCurrent.filter((id) => id !== optionId);
    }

    // Build the updated filter records, starting with the clicked item's category
    const updatedFilterType: Record<string, string[] | unknown> = {
        ...state.filters[filterType],
        [categoryId]: newFilter,
    };
    const updatedOppositeType: Record<string, string[] | unknown> = {
        ...state.filters[oppositeType],
        [categoryId]: newOpposite,
    };

    // Cascade: add/remove all descendant IDs grouped by their categoryId
    if (clickedOption?.subItems && clickedOption.subItems.length > 0) {
        const descendantsByCategory = collectDescendantIdsByCategory(clickedOption.subItems);
        for (const [descCategoryId, descIds] of Object.entries(descendantsByCategory)) {
            const currentDescFilter = (updatedFilterType[descCategoryId] as string[]) || [];
            const currentDescOpposite = (updatedOppositeType[descCategoryId] as string[]) || [];

            if (willBeChecked) {
                // Add all descendant IDs (deduplicated)
                const merged = new Set([...currentDescFilter, ...descIds]);
                updatedFilterType[descCategoryId] = [...merged];
            } else {
                // Remove all descendant IDs
                const toRemove = new Set(descIds);
                updatedFilterType[descCategoryId] = currentDescFilter.filter((id) => !toRemove.has(id));
            }
            // Always remove descendants from opposite side
            const toRemoveOpp = new Set(descIds);
            updatedOppositeType[descCategoryId] = currentDescOpposite.filter((id) => !toRemoveOpp.has(id));
        }
    }

    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: updatedOptions,
            [oppositeType]: oppositeUpdated,
        },
        filters: {
            ...state.filters,
            [filterType]: updatedFilterType,
            [oppositeType]: updatedOppositeType,
        },
    };
}

function handleClearDateRange(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "CLEAR_DATE_RANGE") return state;
    const {filterType, parentCategoryId, categoryId} = action.payload;
    const updatedOptions = updatedDateOptions(
        state.options[filterType],
        parentCategoryId,
        categoryId
    );
    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: updatedOptions,
        },
        filters: {
            ...state.filters,
            [filterType]: {
                ...state.filters[filterType],
                [categoryId]: [],
            },
        },
    };
}

function handleSetDateRange(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "SET_DATE_RANGE") return state;
    const {filterType, parentCategoryId, categoryId, dateRange} = action.payload;
    if (!dateRange?.[0] || !dateRange?.[1] || !isYearValid(dateRange[1])) {
        return state;
    }
    const formattedDateRange =
        dateRange[0] && dateRange[1]
            ? [dateRange[0].toISODate()!, dateRange[1].toISODate()!]
            : [];
    const updatedOptions = updatedDateOptions(
        state.options[filterType],
        parentCategoryId,
        categoryId,
        dateRange
    );
    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: updatedOptions,
        },
        filters: {
            ...state.filters,
            [filterType]: {
                ...state.filters[filterType],
                [categoryId]: formattedDateRange,
            },
        },
    };
}

function handleSetNumberRange(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "SET_NUMBER_RANGE") return state;
    const {filterType, parentCategoryId, categoryId, numberRange} = action.payload;
    const [min, max] = numberRange;

    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
        return state;
    }

    const updatedOptions = updatedNumberRangeOptions(
        state.options[filterType],
        parentCategoryId,
        categoryId,
        numberRange
    );
    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: updatedOptions,
        },
        filters: {
            ...state.filters,
            [filterType]: {
                ...state.filters[filterType],
                [categoryId]: [min.toString(), max.toString()],
            },
        },
    };
}

function handleClearNumberRange(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "CLEAR_NUMBER_RANGE") return state;
    const {filterType, parentCategoryId, categoryId} = action.payload;
    const updatedOptions = updatedNumberRangeOptions(
        state.options[filterType],
        parentCategoryId,
        categoryId
    );
    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: updatedOptions,
        },
        filters: {
            ...state.filters,
            [filterType]: {
                ...state.filters[filterType],
                [categoryId]: [],
            },
        },
    };
}

function handleRemoveFilter(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "REMOVE_FILTER") return state;
    const {filterType, categoryId, optionId} = action.payload;
    const current = state.filters[filterType][categoryId];
    const rootCategoryId = findRootCategoryId(state.options[filterType], categoryId);
    const rootCategory = state.options[filterType].find(
        (category) => category.id === rootCategoryId
    );
    const removedOption = rootCategory
        ? findOptionRecursive(rootCategory.options, optionId)
        : undefined;
    let updated: string[] = [];
    if (Array.isArray(current) && current.length > 0) {
        if (typeof current[0] === "string") {
            updated = (current as string[]).filter((id) => id !== optionId);
        } else {
            updated = [];
        }
    }

    const updatedFilters = {
        ...state.filters[filterType],
        [categoryId]: updated,
    };

    if (removedOption?.subItems && removedOption.subItems.length > 0) {
        const descendantsByCategory = collectDescendantIdsByCategory(removedOption.subItems);
        for (const [descCategoryId, descIds] of Object.entries(descendantsByCategory)) {
            const currentDescendants = updatedFilters[descCategoryId];
            if (
                Array.isArray(currentDescendants) &&
                currentDescendants.every((id) => typeof id === "string")
            ) {
                const idsToRemove = new Set(descIds);
                updatedFilters[descCategoryId] = currentDescendants.filter(
                    (id) => !idsToRemove.has(id)
                );
            }
        }
    }

    return {
        ...state,
        filters: {
            ...state.filters,
            [filterType]: {
                ...updatedFilters,
            },
        },
        options: {
            ...state.options,
            [filterType]: updatedCheckboxOptionsSetChecked(
                state.options[filterType],
                rootCategoryId,
                optionId,
                false
            ),
        },
    };
}

function handleClearFilterType(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "CLEAR_FILTER_TYPE") return state;
    const {filterType, categoryId} = action.payload;
    // Find the root categoryId for the given categoryId
    const rootCategoryId = findRootCategoryId(state.options[filterType], categoryId);
    return {
        ...state,
        filters: {
            ...state.filters,
            [filterType]: {
                ...state.filters[filterType],
                [categoryId]: [],
            },
        },
        options: {
            ...state.options,
            [filterType]: state.options[filterType].map((cat) =>
                cat.id === rootCategoryId
                    ? resetCategoryOptions(cat)
                    : cat
            ),
        },
    };
}

function handleClearAll(state: FilterState): FilterState {
    return {
        ...state,
        filters: {include: {}, exclude: {}},
        options: {
            include: state.options.include.map((cat) => resetCategoryOptions(cat)),
            exclude: state.options.exclude.map((cat) => resetCategoryOptions(cat)),
        },
    };
}

function handleSelectAll(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "SELECT_ALL") return state;
    const {filterType, categoryId} = action.payload;
    const category = state.options[filterType].find(
        (cat) => cat.id === categoryId
    );
    if (!category) return state;

    // Check/uncheck top-level options AND cascade to all subItems
    const allChecked = category.options.every((opt) => opt.checked);
    const newChecked = !allChecked;
    const updatedOptions = category.options.map((opt) => ({
        ...opt,
        checked: newChecked,
        subItems: opt.subItems ? setCheckedRecursive(opt.subItems, newChecked) : undefined,
    }));
    // Build updated filters — group top-level options by their own categoryId
    // (which may differ from the container categoryId, e.g., container "segments" vs option "segment")
    const updatedFilters: Record<string, string[] | unknown> = {
        ...state.filters[filterType],
    };
    const oppositeType = filterType === "include" ? "exclude" : "include";
    const updatedOppositeFilters: Record<string, string[] | unknown> = {
        ...state.filters[oppositeType],
    };
    for (const opt of updatedOptions) {
        if (opt.cascadeOnly) continue;
        const current = (updatedFilters[opt.categoryId] as string[]) || [];
        if (newChecked) {
            const merged = new Set([...current, opt.item]);
            updatedFilters[opt.categoryId] = [...merged];
            // Remove from opposite side
            const oppCurrent = (updatedOppositeFilters[opt.categoryId] as string[]) || [];
            updatedOppositeFilters[opt.categoryId] = oppCurrent.filter((id) => id !== opt.item);
        } else {
            updatedFilters[opt.categoryId] = current.filter((id) => id !== opt.item);
        }
    }

    // Cascade: add/remove all descendant IDs grouped by their categoryId
    for (const opt of category.options) {
        if (opt.subItems && opt.subItems.length > 0) {
            const descendantsByCategory = collectDescendantIdsByCategory(opt.subItems);
            for (const [descCategoryId, descIds] of Object.entries(descendantsByCategory)) {
                const currentDesc = (updatedFilters[descCategoryId] as string[]) || [];
                if (newChecked) {
                    const merged = new Set([...currentDesc, ...descIds]);
                    updatedFilters[descCategoryId] = [...merged];
                    // Remove descendants from opposite side
                    const oppCurrent = (updatedOppositeFilters[descCategoryId] as string[]) || [];
                    const toRemoveOpp = new Set(descIds);
                    updatedOppositeFilters[descCategoryId] = oppCurrent.filter((id) => !toRemoveOpp.has(id));
                } else {
                    const toRemove = new Set(descIds);
                    updatedFilters[descCategoryId] = (currentDesc as string[]).filter(
                        (id) => !toRemove.has(id)
                    );
                }
            }
        }
    }

    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: state.options[filterType].map((cat) =>
                cat.id === categoryId ? {...cat, options: updatedOptions} : cat
            ),
        },
        filters: {
            ...state.filters,
            [filterType]: updatedFilters,
            [oppositeType]: updatedOppositeFilters,
        },
    };
}

function handleSetServerOptions(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "SET_SERVER_OPTIONS") return state;
    const {filterType, categoryId, options} = action.payload;
    const newOptions = addCheckedProperty(options, state.filters[filterType] as Record<string, string[]>);
    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: state.options[filterType].map((cat) =>
                cat.id === categoryId ? {...cat, options: newOptions} : cat
            ),
        },
    };
}

function handleClearServerOptions(state: FilterState, action: FilterAction): FilterState {
    if (action.type !== "CLEAR_SERVER_OPTIONS") return state;
    const {filterType, categoryId} = action.payload;
    const selected = (state.filters[filterType][categoryId] as string[]) || [];
    return {
        ...state,
        options: {
            ...state.options,
            [filterType]: state.options[filterType].map((cat) =>
                cat.id === categoryId
                    ? {
                        ...cat,
                        options: cat.options.filter((opt) => selected.includes(opt.item)),
                    }
                    : cat
            ),
        },
    };
}

function findRootCategoryId(categories: IncludeExcludeFilterCategory[], targetCategoryId: string): string | undefined {
    for (const category of categories) {
        if (category.id === targetCategoryId) return category.id;
        // Recursive search in options/subItems
        const found = findCategoryIdInOptions(category.options, targetCategoryId);
        if (found) return category.id;
    }
    return undefined;
}

function findCategoryIdInOptions(options: FilterOption[], targetCategoryId: string): boolean {
    for (const opt of options) {
        if (opt.categoryId === targetCategoryId) return true;
        if (opt.subItems && findCategoryIdInOptions(opt.subItems, targetCategoryId)) return true;
    }
    return false;
}

/**
 * Check if two category arrays have the same structure (IDs, types, serverSideSearch flags).
 * This helps avoid unnecessary re-initialization when only array references change.
 */
export function areCategoriesStructurallyEqual(
    a: IncludeExcludeFilterCategory[],
    b: IncludeExcludeFilterCategory[]
): boolean {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
        if (
            a[i].id !== b[i].id ||
            a[i].type !== b[i].type ||
            a[i].serverSideSearch !== b[i].serverSideSearch
        ) {
            return false;
        }
    }

    return true;
}

// --- Main Reducer ---
export function filterReducer(
    state: FilterState,
    action: FilterAction
): FilterState {
    switch (action.type) {
        case "INIT":
            return handleInit(state, action);
        case "UPDATE_OPTIONS":
            return handleUpdateOptions(state, action);
        case "TOGGLE_OPTION":
            return handleToggleOption(state, action);
        case "CLEAR_DATE_RANGE":
            return handleClearDateRange(state, action);
        case "SET_DATE_RANGE":
            return handleSetDateRange(state, action);
        case "SET_DATE_OPERATOR":
            return handleSetDateOperator(state, action);
        case "SET_NUMBER_RANGE":
            return handleSetNumberRange(state, action);
        case "CLEAR_NUMBER_RANGE":
            return handleClearNumberRange(state, action);
        case "REMOVE_FILTER":
            return handleRemoveFilter(state, action);
        case "CLEAR_FILTER_TYPE":
            return handleClearFilterType(state, action);
        case "CLEAR_ALL":
            return handleClearAll(state);
        case "SELECT_ALL":
            return handleSelectAll(state, action);
        case "SET_SERVER_OPTIONS":
            return handleSetServerOptions(state, action);
        case "CLEAR_SERVER_OPTIONS":
            return handleClearServerOptions(state, action);
        default:
            return state;
    }
}
